import {
	useQuery,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import type { UseQueryResult, UseMutationResult } from "@tanstack/react-query";
import { useToast } from "@chakra-ui/react";
import {
	getRevisionUploads,
	uploadRevision,
} from "../api/revision-evaluation-api";
import type {
	EvaluationRevisionUpload,
	UploadRevisionPayload,
} from "../types/revision-review.type";

const revisionUploadKeys = {
	all: (rfxId: number, evaluationId: number) =>
		["revision-uploads", rfxId, evaluationId] as const,
	list: (rfxId: number, evaluationId: number) =>
		[...revisionUploadKeys.all(rfxId, evaluationId), "list"] as const,
};

export { revisionUploadKeys };

export const useRevisionUploads = (
	rfxId: number | undefined,
	evaluationId: number | undefined
): UseQueryResult<EvaluationRevisionUpload[]> => {
	return useQuery({
		queryKey: revisionUploadKeys.list(rfxId!, evaluationId!),
		queryFn: () => getRevisionUploads(rfxId!, evaluationId!),
		enabled: !!rfxId && !!evaluationId,
	});
};

export const useUploadRevision = (
	rfxId: number,
	evaluationId: number
): UseMutationResult<
	EvaluationRevisionUpload,
	Error,
	UploadRevisionPayload
> => {
	const queryClient = useQueryClient();
	const toast = useToast();

	return useMutation({
		mutationFn: async (payload: UploadRevisionPayload) => {
			const result = await uploadRevision(rfxId, evaluationId, payload);
			if (!result) {
				throw new Error("Upload failed");
			}
			return result;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: revisionUploadKeys.list(rfxId, evaluationId),
			});
			toast({
				title: "Revision uploaded successfully",
				status: "success",
				duration: 3000,
				isClosable: true,
			});
		},
		onError: () => {
			toast({
				title: "Failed to upload revision",
				description: "Please try again.",
				status: "error",
				duration: 5000,
				isClosable: true,
			});
		},
	});
};
